/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Box, Flex, Heading, Input, Text } from 'theme-ui';
import { useGetAssistantUsageQuery } from '@/store/apiService';
import { Container } from '@/components/styled';
import Button from '@/components/Button';
import { AssistantUsageMeter, SuperAdminAssistantUsage } from '@/types/types';

const PAGE_SIZE = 25;
const WARN_PERCENT = 75;
const CRITICAL_PERCENT = 90;

const formatTokens = (value: number) => value.toLocaleString('sv-SE');

const barColor = (percent: number) => {
    if (percent >= CRITICAL_PERCENT) return 'red.5';
    if (percent >= WARN_PERCENT) return 'orange.5';
    return 'green.5';
};

const Meter: React.FC<{ meter: AssistantUsageMeter | null }> = ({ meter }) => {
    if (!meter) {
        return <Text sx={{ color: 'gray.5' }}>—</Text>;
    }

    if (meter.TokenLimit <= 0) {
        return (
            <Box>
                <Text sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatTokens(meter.TokensUsed)}
                </Text>
                <Text sx={{ display: 'block', color: 'gray.5', fontSize: 0 }}>no limit set</Text>
            </Box>
        );
    }

    const percent = Math.min(100, meter.PercentUsed);

    return (
        <Box sx={{ minWidth: '180px' }}>
            <Flex sx={{ justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Text sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatTokens(meter.TokensUsed)} / {formatTokens(meter.TokenLimit)}
                </Text>
                <Text
                    sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 'bold',
                        color: meter.IsWithinBudget ? 'gray.7' : 'red.6',
                    }}
                >
                    {meter.PercentUsed}%
                </Text>
            </Flex>
            <Box sx={{ height: '6px', borderRadius: 'sm', backgroundColor: 'gray.2' }}>
                <Box
                    sx={{
                        height: '6px',
                        width: `${percent}%`,
                        borderRadius: 'sm',
                        backgroundColor: barColor(meter.PercentUsed),
                    }}
                />
            </Box>
        </Box>
    );
};

const HeaderCell: React.FC = ({ children }) => (
    <Box
        as="th"
        sx={{
            textAlign: 'left',
            p: 3,
            borderBottom: '1px solid',
            borderColor: 'gray.3',
            color: 'gray.6',
            fontSize: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
        }}
    >
        {children}
    </Box>
);

const BodyCell: React.FC = ({ children }) => (
    <Box as="td" sx={{ p: 3, borderBottom: '1px solid', borderColor: 'gray.2' }}>
        {children}
    </Box>
);

const AssistantUsage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [skip, setSkip] = useState(0);

    const { data, isFetching } = useGetAssistantUsageQuery({
        Search: appliedSearch || undefined,
        Skip: skip,
        Take: PAGE_SIZE,
    });

    const rows: SuperAdminAssistantUsage[] = data?.Results ?? [];
    const total = data?.Total ?? 0;
    const windowDays = data?.WindowDays ?? 30;

    const applySearch = () => {
        setSkip(0);
        setAppliedSearch(search.trim());
    };

    return (
        <Container>
            <Heading as="h2" sx={{ mb: 1 }}>
                Assistant token usage
            </Heading>
            <Text sx={{ display: 'block', color: 'gray.6', mb: 3 }}>
                Tokens consumed per company in the current rolling {windowDays}-day window, biggest
                spender first. Companies with no usage in the window follow, sorted by name. Only
                the current window is stored, so there is no history.
            </Text>

            <Flex sx={{ gap: 2, mb: 3, alignItems: 'center' }}>
                <Input
                    value={search}
                    placeholder="Search company name"
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') applySearch();
                    }}
                    sx={{ maxWidth: '320px', backgroundColor: 'gray.1' }}
                />
                <Button type="button" onClick={applySearch}>
                    Search
                </Button>
                {appliedSearch && (
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                            setSearch('');
                            setAppliedSearch('');
                            setSkip(0);
                        }}
                    >
                        Clear
                    </Button>
                )}
            </Flex>

            <Box as="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box as="thead">
                    <Box as="tr">
                        <HeaderCell>Company</HeaderCell>
                        <HeaderCell>AI assistant (admin portal)</HeaderCell>
                        <HeaderCell>Public MCP</HeaderCell>
                    </Box>
                </Box>
                <Box as="tbody">
                    {rows.map((row) => (
                        <Box as="tr" key={row.CompanyId}>
                            <BodyCell>
                                <Text sx={{ display: 'block' }}>{row.CompanyName}</Text>
                                <Text sx={{ color: 'gray.5', fontSize: 0 }}>{row.CompanyId}</Text>
                            </BodyCell>
                            <BodyCell>
                                <Meter meter={row.AiChat} />
                            </BodyCell>
                            <BodyCell>
                                <Meter meter={row.Mcp} />
                            </BodyCell>
                        </Box>
                    ))}
                </Box>
            </Box>

            {!isFetching && rows.length === 0 && (
                <Text sx={{ display: 'block', color: 'gray.5', p: 3 }}>No companies found.</Text>
            )}

            <Flex sx={{ gap: 2, mt: 3, alignItems: 'center' }}>
                <Button
                    variant="secondary"
                    type="button"
                    disabled={skip === 0 || isFetching}
                    onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                >
                    Previous
                </Button>
                <Button
                    variant="secondary"
                    type="button"
                    disabled={skip + PAGE_SIZE >= total || isFetching}
                    onClick={() => setSkip(skip + PAGE_SIZE)}
                >
                    Next
                </Button>
                <Text sx={{ color: 'gray.6' }}>
                    {total === 0
                        ? '0 companies'
                        : `${skip + 1}–${Math.min(skip + PAGE_SIZE, total)} of ${total}`}
                </Text>
            </Flex>
        </Container>
    );
};

export default AssistantUsage;
